import { createContext, useContext, useEffect } from 'react';
import { useRecoilState as useState } from 'recoil';

import { useCurrentUser } from 'hooks/useCurrentUser';
import { splitIoState } from './state';
import { getClientWithState } from './StatefulSplitClient';
import type { StatefulSplitClient, StatefulSplitClientState } from './StatefulSplitClient';
import { logger } from 'modules/logger';
import { IBrowserClient } from '@splitsoftware/splitio/types/splitio';

// @ts-expect-error [EN-10263] - TS2322 - Type 'Context<null>' is not assignable to type 'Context<StatefulSplitClient | null>'.
export const MockSplitClientContext: React.Context<StatefulSplitClient | null> =
  createContext(null);

export const useSplitClient = (): [Nullable<IBrowserClient>, boolean, boolean] => {
  // To get around exhaustive-deps we can use the 'useState' alias but using
  // recoil state (as they are equivalent). This way we do not have to ignore the line
  const [state, setState] = useState(splitIoState);
  const { data } = useCurrentUser();
  const email = data?.me.email;

  const mockedSplitClient = useContext(MockSplitClientContext);

  useEffect(() => {
    if (email == null) {
      return;
    }

    const statefulClient = getClientWithState(email);

    setState((state) => {
      if (
        state.email === email &&
        state.client === statefulClient &&
        state.ready === (statefulClient.state === 'ready') &&
        state.timeout === (statefulClient.state === 'timeout')
      ) {
        return state;
      }

      return {
        ...state,
        client: statefulClient,
        email,
        ready: statefulClient.state === 'ready',
        timeout: statefulClient.state === 'timeout',
      };
    });

    if (statefulClient.state === 'pending') {
      const onStateChange = (state: StatefulSplitClientState) => {
        if (state === 'ready') {
          setState((state) => ({ ...state, ready: true }));
        } else if (state === 'timeout') {
          setState((state) => ({ ...state, timeout: true }));
        }
      };

      statefulClient.onStateChange(onStateChange);
      return () => statefulClient.offStateChange(onStateChange);
    } else {
      // this message is spammy so it will hide in debug
      logger.debug(`Legacy Split Client: already ${statefulClient.state}`);
    }
  }, [email]);

  return mockedSplitClient
    ? [
        mockedSplitClient.splitClient,
        mockedSplitClient.state === 'ready',
        mockedSplitClient.state === 'timeout',
      ]
    : [state.email === email ? state.client?.splitClient : null, state.ready, state.timeout];
};
